import { Injectable, ErrorHandler } from '@angular/core';
import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, finalize, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';

// Services
import { DataService } from '../../shared/services/data.service';
import { SessionData } from '../../shared/model/entry.model';
import { NewRelicService } from '../../shared/services/new-relic.service';
import { Router } from '@angular/router';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor, ErrorHandler {
  private totalRequest = 0; // Maintain the number of API Requests
  sessionData: SessionData = {};

  constructor(
    private dataService: DataService,
    private newRelicService: NewRelicService,
    private router: Router,
  ) {}

  handleError(error: any): void {
    console.error(error);
    this.sessionData = this.dataService.getData('sessionData') || {};
    const customAttributes = {
      underId: this.sessionData?.guid || 'N/A',
      policyNumber: this.sessionData?.policyDetails?.clientCaseId || 'N/A',
      trace: error,
    };

    let message;
    if (error?.message?.length) {
      message = `${error?.message}: ${this.router.url?.split('?')[0]}`;
    } else {
      message = `Runtime error in ${this.router.url?.split('?')[0]}`;
    }
    this.newRelicService.captureLogEvent(message, 'trace', customAttributes);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    this.sessionData = this.dataService.getData('sessionData') || {};
    const message = `${request?.method?.toUpperCase()}: ${request?.url?.split('.com')[1]}`;
    let requestBody = '';

    if (request?.body) {
      if (typeof request?.body == 'object') {
        requestBody = this.objectToString(request?.body);
      }

      if (typeof request?.body == 'string') {
        requestBody = request?.body;
      }
    }

    const customAttributes = {
      underId: this.sessionData?.guid || 'N/A',
      policyNumber: this.sessionData?.policyDetails?.clientCaseId || 'N/A',
      requestUrl: request?.url?.split('.com')[1],
      requestMethod: request?.method,
      requestBody,
    };

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          customAttributes['statusCode'] = event?.status;
          this.newRelicService.captureLogEvent(message, 'info', customAttributes);
        }
      }),
      catchError((errorData: HttpErrorResponse) => {
        console.log('💬 ~ catchError ~ errorData', errorData);
        const errorMessage = this.setError(errorData);
        this.newRelicService.captureLogEvent(message, 'error', customAttributes);

        return throwError(errorMessage);
      }),
    );
  }

  setError(errorData: HttpErrorResponse) {
    let errorMessage = 'Sorry, unknown error occured!';

    if (errorData.error instanceof ErrorEvent) {
      // Client side error catching
      errorMessage = errorData.error.message;
    } else {
      // Server side error catching
      if (errorData.status === 500) {
        errorMessage = 'An error has occurred. Please contact BTS Production Support for assistance.';
      } else if (errorData.status !== 0) {
        if (errorData.error.error?.errorDescription) {
          errorMessage = `${errorData.error.error.errorDescription}`;
        } else {
          errorMessage = `${errorData.status} (${errorData.statusText})`;
        }
      }
    }

    return errorMessage;
  }

  objectToString(obj: any) {
    const sensitiveKeys = ['ssn', 'socialsecuritynumber'];
    const sanitized = { ...obj };
    for (const key of Object.keys(sanitized)) {
      if (sensitiveKeys.includes(key.toLowerCase())) {
        const val = String(sanitized[key]).replace(/\D/g, '');
        sanitized[key] = val.length >= 4 ? '***-**-' + val.slice(-4) : val;
      }
    }
    return JSON.stringify(sanitized, null, 2);
  }
}
